import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  FormControlLabel,
  Switch,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Avatar,
  Chip
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import CreateIcon from '@mui/icons-material/Create';
import { usePlayer } from '../../context/PlayerContext';
import { useMasterPlayer } from '../../context/MasterPlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './AddPlayerDialog.scss';

const AddPlayerDialog = ({ open, onClose }) => {
  const { addPlayer, players } = usePlayer();
  const { masterPlayers } = useMasterPlayer();
  const [selectionMode, setSelectionMode] = useState('freeform'); // 'freeform' or 'masterlist'
  const [selectedMasterPlayer, setSelectedMasterPlayer] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [defaultBuyIn, setDefaultBuyIn] = useState('200');
  const [includeDefaultBuyIn, setIncludeDefaultBuyIn] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  
  const MIN_BUYIN = 100;
  const MAX_BUYIN = 500;

  const validateBuyInAmount = (value) => {
    const buyInAmount = parseFloat(value);
    
    if (!value || isNaN(buyInAmount) || buyInAmount <= 0) {
      return 'Please enter a valid buy-in amount';
    }

    if (buyInAmount < MIN_BUYIN) {
      return `Minimum buy-in amount is $${MIN_BUYIN}`;
    }

    if (buyInAmount > MAX_BUYIN) {
      return `Maximum buy-in amount is $${MAX_BUYIN}`;
    }

    return '';
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    let finalPlayerName;
    
    if (selectionMode === 'masterlist') {
      if (!selectedMasterPlayer) {
        setError('Please select a player from the master list');
        return;
      }
      finalPlayerName = selectedMasterPlayer.name;
    } else {
      if (!playerName.trim()) {
        setError('Player name is required');
        return;
      }
      finalPlayerName = playerName.trim();
    }

    if (players.some(player => 
      player.name.toLowerCase() === finalPlayerName.toLowerCase()
    )) {
      setError('Player already exists in this session');
      return;
    }

    if (includeDefaultBuyIn) {
      const validationError = validateBuyInAmount(defaultBuyIn);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    const buyInAmount = includeDefaultBuyIn ? parseFloat(defaultBuyIn) : null;
    
    addPlayer(finalPlayerName, includeDefaultBuyIn ? {
      amount: buyInAmount,
      note: note.trim()
    } : null);
    
    // Reset form
    setSelectionMode('freeform');
    setSelectedMasterPlayer(null);
    setPlayerName('');
    setDefaultBuyIn('200');
    setIncludeDefaultBuyIn(true);
    setNote('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setSelectionMode('freeform');
    setSelectedMasterPlayer(null);
    setPlayerName('');
    setDefaultBuyIn('200');
    setIncludeDefaultBuyIn(true);
    setNote('');
    setError('');
    onClose();
  };

  const handleNameChange = (event) => {
    setPlayerName(event.target.value);
    if (error && error.includes('name')) setError('');
  };

  const handleBuyInChange = (event) => {
    const value = event.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setDefaultBuyIn(value);
      
      // Clear error when user starts typing
      if (error && error.includes('amount')) {
        setError('');
      }
      
      // Show real-time validation for better UX
      if (value && !isNaN(parseFloat(value)) && includeDefaultBuyIn) {
        const validationError = validateBuyInAmount(value);
        if (validationError) {
          setError(validationError);
        }
      }
    }
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSelectionMode(newMode);
      setSelectedMasterPlayer(null);
      setPlayerName('');
      setError('');
    }
  };

  const handleMasterPlayerChange = (event, newValue) => {
    setSelectedMasterPlayer(newValue);
    if (error && (error.includes('select') || error.includes('Player already exists'))) {
      setError('');
    }
  };

  // Filter out players that already exist in the current session
  const existingPlayerNames = players.map(p => p.name.toLowerCase());
  const availableMasterPlayers = masterPlayers.filter(
    masterPlayer => !existingPlayerNames.includes(masterPlayer.name.toLowerCase())
  );

  const isValidForm = () => {
    if (selectionMode === 'masterlist') {
      if (!selectedMasterPlayer) return false;
    } else {
      if (!playerName.trim()) return false;
    }
    
    if (includeDefaultBuyIn) {
      const buyInAmount = parseFloat(defaultBuyIn);
      return defaultBuyIn && !isNaN(buyInAmount) && 
             buyInAmount >= MIN_BUYIN && buyInAmount <= MAX_BUYIN;
    }
    
    return true;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="add-player-dialog"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            <PersonAddIcon className="title-icon" />
            <Typography variant="h5">
              Add New Player
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Box className="form-fields">
            {/* Mode Selection */}
            <Box className="mode-selection">
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                How would you like to add the player?
              </Typography>
              <ToggleButtonGroup
                value={selectionMode}
                exclusive
                onChange={handleModeChange}
                className="mode-toggle"
                fullWidth
              >
                <ToggleButton value="freeform" className="mode-button">
                  <CreateIcon sx={{ mr: 1 }} />
                  Type Name
                </ToggleButton>
                <ToggleButton value="masterlist" className="mode-button">
                  <GroupIcon sx={{ mr: 1 }} />
                  From Master List
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Player Input based on mode */}
            {selectionMode === 'freeform' ? (
              <TextField
                autoFocus
                fullWidth
                label="Player Name"
                variant="outlined"
                value={playerName}
                onChange={handleNameChange}
                error={!!error && error.includes('name')}
                helperText={error && error.includes('name') ? error : ''}
                placeholder="Enter player name"
                className="name-input"
              />
            ) : (
              <Autocomplete
                fullWidth
                options={availableMasterPlayers}
                getOptionLabel={(option) => option.name}
                value={selectedMasterPlayer}
                onChange={handleMasterPlayerChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Player"
                    variant="outlined"
                    error={!!error && (error.includes('select') || error.includes('Player already exists'))}
                    helperText={error && (error.includes('select') || error.includes('Player already exists')) ? error : 
                      availableMasterPlayers.length === 0 ? 'No new players available from master list' : 
                      'Choose from your master player list'
                    }
                    placeholder="Search and select a player"
                    className="master-player-input"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} className="master-player-option">
                    <Avatar
                      sx={{
                        bgcolor: option.profileColor,
                        color: getContrastTextColor(option.profileColor),
                        width: 36,
                        height: 36,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getInitials(option.name)}
                    </Avatar>
                    <Box className="player-info">
                      <Typography variant="body1">{option.name}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                disabled={availableMasterPlayers.length === 0}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={includeDefaultBuyIn}
                  onChange={(e) => {
                    setIncludeDefaultBuyIn(e.target.checked);
                    if (error && error.includes('amount')) {
                      setError('');
                    }
                  }}
                  color="primary"
                />
              }
              label="Add initial buy-in"
              className="switch-control"
            />
            
            {includeDefaultBuyIn && (
              <>
                <Alert severity="info" className="validation-info">
                  Buy-in amount must be between ${MIN_BUYIN} and ${MAX_BUYIN}
                </Alert>
                
                <TextField
                  fullWidth
                  label="Initial Buy-in Amount"
                  variant="outlined"
                  value={defaultBuyIn}
                  onChange={handleBuyInChange}
                  error={!!error && error.includes('amount')}
                  helperText={
                    error && error.includes('amount') 
                      ? error 
                      : `Enter amount between $${MIN_BUYIN} - $${MAX_BUYIN}`
                  }
                  placeholder="200.00"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        $
                      </InputAdornment>
                    ),
                  }}
                  className="buyin-input"
                />
                
                <TextField
                  fullWidth
                  label="Note (Optional)"
                  variant="outlined"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note for the initial buy-in..."
                  multiline
                  rows={2}
                  className="note-input"
                />
              </>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={!isValidForm()}
          >
            Add Player
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddPlayerDialog;